import type { IUserSummary } from '../types/global/helper';
import type {
    AdminAdvancedSearchInclude,
    IAdminAdvancedSearchDiagnostics,
    IAdminAdvancedSearchFilterOptions,
    IAdminAdvancedSearchResult,
    IAdminAdvancedSearchUser,
    IAdminUserAdvancedSearchPayload,
} from '../types/global/adminUserAdvancedSearch.types';
import { unwrapApiPayload } from './campaignHostSubscriptionApi';

export const DEFAULT_ADVANCED_SEARCH_INCLUDE: AdminAdvancedSearchInclude[] = [
    'email',
    'apps',
    'last_signed_in',
    'profiles',
    'subscription_summary',
    'video_count',
    'tenant_memberships',
];

/** Reject unset Places defaults / null-island [0,0]. */
export function isUsableAdvancedSearchCoordinates(
    coordinates: number[] | undefined | null,
): coordinates is [number, number] {
    if (!Array.isArray(coordinates) || coordinates.length !== 2) return false;
    const lng = Number(coordinates[0]);
    const lat = Number(coordinates[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
    if (lng < -180 || lng > 180 || lat < -90 || lat > 90) return false;
    if (lng === 0 && lat === 0) return false;
    return true;
}

export function emptyAdvancedSearchPayload(): IAdminUserAdvancedSearchPayload {
    return {
        include: [...DEFAULT_ADVANCED_SEARCH_INCLUDE],
        page: 1,
        limit: 25,
        sort: 'last_signed_in_desc',
    };
}

export function hasAdvancedSearchCriteria(payload: IAdminUserAdvancedSearchPayload): boolean {
    if (payload.searchTerm?.trim()) return true;
    if (payload.contest && (payload.contest.any || payload.contest.none || payload.contest.contest_ids?.length)) {
        return true;
    }
    if (payload.vote && (payload.vote.any || payload.vote.none || payload.vote.contest_ids?.length)) {
        return true;
    }
    if (
        payload.campaign &&
        (payload.campaign.any ||
            payload.campaign.none ||
            payload.campaign.campaign_ids?.length ||
            payload.campaign.statuses?.length)
    ) {
        return true;
    }
    if (
        payload.subscription &&
        (payload.subscription.has_any ||
            payload.subscription.none ||
            payload.subscription.kinds?.length ||
            payload.subscription.statuses?.length ||
            payload.subscription.campaign_host_package_slugs?.length)
    ) {
        return true;
    }
    if (payload.near?.address_contains?.trim()) return true;
    if (
        isUsableAdvancedSearchCoordinates(payload.near?.coordinates) &&
        payload.near?.radius_km != null &&
        Number(payload.near.radius_km) > 0
    ) {
        return true;
    }
    if (
        payload.work_profile &&
        (payload.work_profile.none ||
            payload.work_profile.has_published_profile != null ||
            payload.work_profile.profile_types?.length)
    ) {
        return true;
    }
    if (payload.apps) {
        const a = payload.apps;
        if (a.any_of?.length || a.all_of?.length) return true;
        if (a.thspian != null || a.meeteor != null || a.audme != null || a.buzzbridge != null) return true;
    }
    if (
        payload.tenant &&
        (payload.tenant.belongs_to_tenant != null ||
            payload.tenant.tenant_ids?.length ||
            Boolean(payload.tenant.status))
    ) {
        return true;
    }
    if (
        payload.media &&
        (payload.media.has_video != null ||
            payload.media.min_video_count != null ||
            payload.media.max_video_count != null)
    ) {
        return true;
    }
    if (
        payload.last_signed_in &&
        (payload.last_signed_in.within_days != null || payload.last_signed_in.after || payload.last_signed_in.before)
    ) {
        return true;
    }
    if (payload.audition && (payload.audition.any || payload.audition.none || payload.audition.audition_ids?.length)) {
        return true;
    }
    return false;
}

/** Strip empty nested objects so the API gets a clean AND filter body. */
export function compactAdvancedSearchPayload(
    payload: IAdminUserAdvancedSearchPayload,
    options: { forAudience?: boolean } = {},
): IAdminUserAdvancedSearchPayload {
    const out: IAdminUserAdvancedSearchPayload = {};

    if (!options.forAudience) {
        out.page = payload.page ?? 1;
        out.limit = Math.min(payload.limit ?? 25, 100);
        out.sort = payload.sort ?? 'last_signed_in_desc';
        out.include = payload.include?.length ? payload.include : [...DEFAULT_ADVANCED_SEARCH_INCLUDE];
    }

    if (payload.searchTerm?.trim()) out.searchTerm = payload.searchTerm.trim();

    const contest = compactContestLike(payload.contest, 'contest_ids');
    if (contest) out.contest = contest;
    const vote = compactContestLike(payload.vote, 'contest_ids');
    if (vote) out.vote = vote;
    const campaign = compactCampaign(payload.campaign);
    if (campaign) out.campaign = campaign;
    const subscription = compactSubscription(payload.subscription);
    if (subscription) out.subscription = subscription;
    if (payload.near) {
        const near: NonNullable<IAdminUserAdvancedSearchPayload['near']> = {};
        if (payload.near.address_contains?.trim()) {
            near.address_contains = payload.near.address_contains.trim();
        }
        if (isUsableAdvancedSearchCoordinates(payload.near.coordinates)) {
            // Only keep near-place when radius is set.
            if (payload.near.radius_km != null && Number(payload.near.radius_km) > 0) {
                near.coordinates = [Number(payload.near.coordinates[0]), Number(payload.near.coordinates[1])];
                near.radius_km = Number(payload.near.radius_km);
            }
        }
        if (Object.keys(near).length) out.near = near;
    }
    const work_profile = compactWorkProfile(payload.work_profile);
    if (work_profile) out.work_profile = work_profile;
    const apps = compactApps(payload.apps);
    if (apps) out.apps = apps;
    const tenant = compactTenant(payload.tenant);
    if (tenant) out.tenant = tenant;
    const media = compactMedia(payload.media);
    if (media) out.media = media;
    const last_signed_in = compactLastSignedIn(payload.last_signed_in);
    if (last_signed_in) out.last_signed_in = last_signed_in;
    const audition = compactContestLike(payload.audition, 'audition_ids');
    if (audition) out.audition = audition;

    return out;
}

function truthyFlag(v: unknown): boolean {
    return v === true;
}

/** Contest / vote / audition: keep ids, any:true, or none:true only. */
function compactContestLike<T extends { any?: boolean; none?: boolean }>(
    value: T | undefined | null,
    idKey: 'contest_ids' | 'audition_ids',
): Partial<T> | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const record = value as T & Record<string, unknown>;
    const out: Record<string, unknown> = {};
    const rawIds = record[idKey];
    const ids = Array.isArray(rawIds) ? rawIds.map(String).filter(Boolean) : [];
    if (truthyFlag(record.none)) {
        out.none = true;
        return out as Partial<T>;
    }
    if (ids.length) {
        out[idKey] = ids;
        // Specific ids already scope the filter — do not also send any:true.
        return out as Partial<T>;
    }
    if (truthyFlag(record.any)) out.any = true;
    return Object.keys(out).length ? (out as Partial<T>) : undefined;
}

function compactCampaign(
    value: IAdminUserAdvancedSearchPayload['campaign'],
): IAdminUserAdvancedSearchPayload['campaign'] | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const out: NonNullable<IAdminUserAdvancedSearchPayload['campaign']> = {};
    if (truthyFlag(value.none)) {
        return { none: true };
    }
    const ids = Array.isArray(value.campaign_ids) ? value.campaign_ids.map(String).filter(Boolean) : [];
    const statuses = Array.isArray(value.statuses) ? value.statuses.map(String).filter(Boolean) : [];
    if (ids.length) out.campaign_ids = ids;
    if (statuses.length) out.statuses = statuses;
    // any is only needed when there are no ids (statuses alone are enough on the API).
    if (!ids.length && truthyFlag(value.any)) out.any = true;
    return Object.keys(out).length ? out : undefined;
}

function compactSubscription(
    value: IAdminUserAdvancedSearchPayload['subscription'],
): IAdminUserAdvancedSearchPayload['subscription'] | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const out: NonNullable<IAdminUserAdvancedSearchPayload['subscription']> = {};
    if (truthyFlag(value.none)) return { none: true };
    if (truthyFlag(value.has_any)) out.has_any = true;
    if (value.kinds?.length) out.kinds = value.kinds;
    if (value.statuses?.length) out.statuses = value.statuses;
    if (value.campaign_host_package_slugs?.length) {
        out.campaign_host_package_slugs = value.campaign_host_package_slugs;
    }
    return Object.keys(out).length ? out : undefined;
}

function compactWorkProfile(
    value: IAdminUserAdvancedSearchPayload['work_profile'],
): IAdminUserAdvancedSearchPayload['work_profile'] | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const out: NonNullable<IAdminUserAdvancedSearchPayload['work_profile']> = {};
    if (truthyFlag(value.none)) return { none: true };
    if (value.profile_types?.length) out.profile_types = value.profile_types;
    if (value.has_published_profile === true || value.has_published_profile === false) {
        out.has_published_profile = value.has_published_profile;
    }
    return Object.keys(out).length ? out : undefined;
}

function compactApps(
    value: IAdminUserAdvancedSearchPayload['apps'],
): IAdminUserAdvancedSearchPayload['apps'] | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const out: NonNullable<IAdminUserAdvancedSearchPayload['apps']> = {};
    if (value.any_of?.length) out.any_of = value.any_of;
    if (value.all_of?.length) out.all_of = value.all_of;
    (['thspian', 'meeteor', 'audme', 'buzzbridge'] as const).forEach(app => {
        if (value[app] === true) out[app] = true;
    });
    return Object.keys(out).length ? out : undefined;
}

function compactTenant(
    value: IAdminUserAdvancedSearchPayload['tenant'],
): IAdminUserAdvancedSearchPayload['tenant'] | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const out: NonNullable<IAdminUserAdvancedSearchPayload['tenant']> = {};
    if (value.belongs_to_tenant === true || value.belongs_to_tenant === false) {
        out.belongs_to_tenant = value.belongs_to_tenant;
    }
    if (value.tenant_ids?.length) out.tenant_ids = value.tenant_ids;
    if (value.status) out.status = value.status;
    return Object.keys(out).length ? out : undefined;
}

function compactMedia(
    value: IAdminUserAdvancedSearchPayload['media'],
): IAdminUserAdvancedSearchPayload['media'] | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const out: NonNullable<IAdminUserAdvancedSearchPayload['media']> = {};
    if (value.has_video === true || value.has_video === false) out.has_video = value.has_video;
    if (value.min_video_count != null && Number(value.min_video_count) >= 0) {
        out.min_video_count = Number(value.min_video_count);
    }
    if (value.max_video_count != null && Number(value.max_video_count) >= 0) {
        out.max_video_count = Number(value.max_video_count);
    }
    return Object.keys(out).length ? out : undefined;
}

function compactLastSignedIn(
    value: IAdminUserAdvancedSearchPayload['last_signed_in'],
): IAdminUserAdvancedSearchPayload['last_signed_in'] | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const out: NonNullable<IAdminUserAdvancedSearchPayload['last_signed_in']> = {};
    if (value.within_days != null && Number(value.within_days) > 0) {
        out.within_days = Number(value.within_days);
    }
    if (value.after) out.after = value.after;
    if (value.before) out.before = value.before;
    return Object.keys(out).length ? out : undefined;
}

/** Human-readable active filters for the results panel (AND semantics). */
export function describeAdvancedSearchFilters(payload: IAdminUserAdvancedSearchPayload): string[] {
    const c = compactAdvancedSearchPayload(payload, { forAudience: true });
    const parts: string[] = [];
    if (c.searchTerm) parts.push(`search “${c.searchTerm}”`);
    if (c.contest?.contest_ids?.length) parts.push(`contest (${c.contest.contest_ids.length} id)`);
    else if (c.contest?.any) parts.push('contest entrant (any)');
    else if (c.contest?.none) parts.push('no contest entry');
    if (c.vote?.contest_ids?.length) parts.push(`voted in contest (${c.vote.contest_ids.length} id)`);
    else if (c.vote?.any) parts.push('contest voter (any)');
    else if (c.vote?.none) parts.push('never voted');
    if (c.campaign?.campaign_ids?.length) parts.push(`campaign (${c.campaign.campaign_ids.length} id)`);
    else if (c.campaign?.any) parts.push('campaign participant (any)');
    if (c.campaign?.statuses?.length) parts.push(`campaign status: ${c.campaign.statuses.join(', ')}`);
    if (c.campaign?.none) parts.push('no campaign participation');
    if (c.audition?.audition_ids?.length) parts.push(`audition (${c.audition.audition_ids.length} id)`);
    else if (c.audition?.any) parts.push('audition applicant (any)');
    else if (c.audition?.none) parts.push('no audition');
    if (c.media?.has_video === true) parts.push('has video');
    if (c.tenant?.belongs_to_tenant === false) parts.push('not in any tenant');
    else if (c.tenant?.belongs_to_tenant === true || c.tenant?.tenant_ids?.length) parts.push('tenant member');
    if (c.work_profile?.profile_types?.length) {
        parts.push(`profile: ${c.work_profile.profile_types.join(', ')}`);
    }
    if (c.subscription?.has_any || c.subscription?.kinds?.length) parts.push('subscription');
    if (c.last_signed_in) parts.push('last signed in');
    if (c.near) parts.push('near place');
    if (c.apps) parts.push('apps');
    return parts;
}

export function advancedSearchUserToSummary(user: IAdminAdvancedSearchUser): IUserSummary {
    return {
        _id: user._id,
        id: user._id,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        userName: user.userName || '',
        email: user.email,
        profile_picture: user.profile_picture || '',
        role: user.role || 'user',
        audme_profile: '',
    };
}

function parseFacetPage(raw: unknown): IAdminAdvancedSearchFilterOptions['contests'] {
    if (!raw || typeof raw !== 'object') return { items: [], total: 0, page: 1, limit: 20 };
    const r = raw as Record<string, unknown>;
    const items = Array.isArray(r.items) ? (r.items as any[]) : [];
    return {
        items: items.map(item => ({
            _id: String(item._id ?? item.id ?? ''),
            title: item.title,
            name: item.name,
            slug: item.slug,
            status: item.status,
            participant_count: item.participant_count,
            applicant_count: item.applicant_count,
        })),
        total: Number(r.total ?? items.length),
        page: Number(r.page ?? 1),
        limit: Number(r.limit ?? 20),
    };
}

export function normalizeFilterOptions(raw: unknown): IAdminAdvancedSearchFilterOptions {
    const data = (unwrapApiPayload(raw) ?? raw) as Record<string, unknown>;
    if (!data || typeof data !== 'object') return {};
    return {
        enums: (data.enums as IAdminAdvancedSearchFilterOptions['enums']) ?? undefined,
        contests: data.contests ? parseFacetPage(data.contests) : undefined,
        campaigns: data.campaigns ? parseFacetPage(data.campaigns) : undefined,
        subscription_packages: data.subscription_packages as IAdminAdvancedSearchFilterOptions['subscription_packages'],
        tenants: data.tenants ? parseFacetPage(data.tenants) : undefined,
        auditions: data.auditions ? parseFacetPage(data.auditions) : undefined,
    };
}

function normalizeDiagnostics(raw: unknown): IAdminAdvancedSearchDiagnostics | undefined {
    if (!raw || typeof raw !== 'object') return undefined;
    const d = raw as Record<string, unknown>;
    return {
        filter_counts: d.filter_counts as IAdminAdvancedSearchDiagnostics['filter_counts'],
        after_relational_intersect:
            d.after_relational_intersect != null ? Number(d.after_relational_intersect) : undefined,
        after_user_match: d.after_user_match != null ? Number(d.after_user_match) : undefined,
        after_exclusions: d.after_exclusions != null ? Number(d.after_exclusions) : undefined,
        candidates_ignoring_last_signed_in:
            d.candidates_ignoring_last_signed_in != null ? Number(d.candidates_ignoring_last_signed_in) : undefined,
        candidates_in_db: d.candidates_in_db != null ? Number(d.candidates_in_db) : undefined,
        candidates_not_deleted: d.candidates_not_deleted != null ? Number(d.candidates_not_deleted) : undefined,
        candidates_deleted: d.candidates_deleted != null ? Number(d.candidates_deleted) : undefined,
        notes: Array.isArray(d.notes) ? d.notes.map(String) : undefined,
    };
}

export function normalizeAdvancedSearchResult(raw: unknown): IAdminAdvancedSearchResult {
    const data = (unwrapApiPayload(raw) ?? raw) as Record<string, unknown>;
    const users = Array.isArray(data?.users) ? (data.users as IAdminAdvancedSearchUser[]) : [];
    const pagination = (data?.pagination as IAdminAdvancedSearchResult['pagination']) ?? {
        page: 1,
        limit: 25,
        total: users.length,
        total_pages: 1,
    };
    return {
        users,
        pagination,
        applied_filters: (data?.applied_filters as IAdminAdvancedSearchResult['applied_filters']) ?? undefined,
        diagnostics: normalizeDiagnostics(data?.diagnostics),
    };
}

/** @deprecated Prefer actions from redux/adminUserSearch/actions via useDispatchPromise. */
export { fetchAdminAdvancedSearchFilterOptions, postAdminAdvancedSearch } from '../redux/adminUserSearch/actions';
